import { h } from "preact";
import { useMemo, useState, useEffect } from "preact/hooks";
import { formatMoney } from "utils/storefront/format-resource";
import IconThreshold from "utils/icon/icon-threshold.svg";
import { withCartContext } from "./cart-context";

export const CartThreshold = ({
  cart,
  active,
  threshold,
  thresholdAmount,
  beforeMessage,
  afterMessage,
  startMessage,
  rewardMessage,
  cartPage,
  showProgress,
  showMessage,
  setIsThresholdFull,
  fsThreshold,
}) => {
  if (!active) {
    return null;
  }
  const [customerDetails, setCustomerDetails] = useState(null);
  let availableProgress = true;
  let availableMessage = true;

  if (cartPage) {
    availableProgress = showProgress;
    availableMessage = showMessage;
  }

  document.addEventListener("swell:setup", () => {
    setCustomerDetails(window?.swellAPI?.getCustomerDetails());
  });

  useEffect(() => {
    setCustomerDetails(window?.swellAPI?.getCustomerDetails());
  }, [cart.lines]);

  const { message, percent, fsPercent } = useMemo(() => {
    if (cart.state.total_price === 0) {
      return {
        message: startMessage.replace(
          "[amount]",
          formatMoney(
            { amount: thresholdAmount * 100 - cart.state.original_total_price },
            true,
            true
          )
        ),
        percent: 0,
        fsPercent: 0,
      };
    }
    if (cart.state.total_price >= thresholdAmount * 100) {
      return {
        message: afterMessage,
        percent: 100,
        fsPercent:
          (cart.state.total_price / (fsThreshold?.thresholdAmount * 100)) * 100,
      };
    }
    return {
      message: beforeMessage.replace(
        "[amount]",
        formatMoney(
          { amount: thresholdAmount * 100 - cart.state.original_total_price },
          true,
          true
        )
      ),
      percent:
        (cart.state.original_total_price / (thresholdAmount * 100)) * 100,
      fsPercent:
        (cart.state.total_price / (fsThreshold?.thresholdAmount * 100)) * 100,
    };
  }, [cart.state, thresholdAmount, customerDetails?.email]);

  useEffect(() => {
    fsThreshold?.active
      ? setIsThresholdFull?.(fsPercent >= 100)
      : setIsThresholdFull?.(true);
  }, [fsPercent]);

  const states = useMemo(() => {
    return {
      LoggedIn: (
        <div className="relative threshold-message overflow-hidden">
          {cart?.state?.item_count > 0 && availableMessage && rewardMessage && (
            <div className="relative overflow-hidden">
              <div className="bg-supergoop-yellow text-center px-5 py-2.5 flex items-center justify-center">
                <div
                  className="body-xs font-medium text-grey-2A"
                  dangerouslySetInnerHTML={{ __html: rewardMessage }}
                ></div>
              </div>
            </div>
          )}

          <div
            className={`relative overflow-hidden ${
              cartPage ? " " : "border-b border-grey-D0 "
            } ${
              cartPage && availableProgress && !availableMessage
                ? ""
                : cartPage
                ? "lg:hidden"
                : ""
            }`}
          >
            <div
              className={`absolute w-full h-full top-0 left-0 
              text-supergoop-blue z-10 
              ${
                percent === 100 ? " threshold-anim mt-0 " : "hidden -mt-[35px] "
              }`}
            >
              <div className="absolute scale-[1] left-6 top-6 rotate-12 opacity-90">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.3] left-1/2 top-3 opacity-50">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.4] left-2/3 top-14 -rotate-6 opacity-60">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.7] right-0 top-6 opacity-75">
                <IconThreshold />
              </div>
            </div>
            <div className="bg-grey-F7 px-5 py-4 ">
              <div
                className="body-xs text-grey-2A  ease-in-out duration-1000 text-center"
                data-target="text-content"
                dangerouslySetInnerHTML={{ __html: message }}
              ></div>
              <div
                className={`h-1.5 mt-2.5 rounded relative mb-1 bg-grey-B4 ${
                  percent === 100 ? "hidden" : ""
                }`}
              >
                <div
                  className="h-full rounded bg-supergoop-blue ease-in-out duration-500"
                  style={{ width: `${percent}%` }}
                ></div>
              </div>
              <div
                className={`w-full flex justify-between text-grey-5C body-xs  ${
                  percent === 100 ? "hidden" : ""
                }`}
              >
                <div>
                  {formatMoney({ amount: 0 }, false).replace(/\.\d+$/, "")}
                </div>
                <div>
                  {formatMoney({ amount: threshold }, false).replace(
                    /\.\d+$/,
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      LoggedOut: (
        <div className="relative  threshold-message overflow-hidden">
          {cart?.state?.item_count > 0 && availableMessage && (
            <div className="relative overflow-hidden">
              <div className="bg-supergoop-yellow text-center px-5 py-2.5 flex items-center justify-center">
                <div
                  className="body-xs font-medium text-grey-2A"
                  dangerouslySetInnerHTML={{ __html: rewardMessage }}
                ></div>
              </div>
            </div>
          )}

          <div
            className={`relative overflow-hidden ${
              cartPage ? " " : "border-b border-grey-D0 "
            } ${
              cartPage && availableProgress && !availableMessage
                ? ""
                : cartPage
                ? "lg:hidden"
                : ""
            }`}
          >
            <div
              className={`absolute w-full h-full top-0 left-0 
              text-supergoop-blue z-10 
              ${
                percent === 100 ? " threshold-anim mt-0 " : "hidden -mt-[35px] "
              }`}
            >
              <div className="absolute scale-[1] left-6 top-6 rotate-12 opacity-90">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.3] left-1/2 top-3 opacity-50">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.4] left-2/3 top-14 -rotate-6 opacity-60">
                <IconThreshold />
              </div>
              <div className="absolute scale-[0.7] right-0 top-6 opacity-75">
                <IconThreshold />
              </div>
            </div>
            <div className="bg-grey-F7 px-5 py-4 ">
              <div
                className="body-xs text-grey-2A  ease-in-out duration-1000 text-center"
                data-target="text-content"
                dangerouslySetInnerHTML={{ __html: message }}
              ></div>
              <div
                className={`h-1.5 mt-2.5 rounded relative mb-1 bg-grey-B4  ${
                  percent === 100 ? "hidden" : ""
                }`}
              >
                <div
                  className="h-full rounded bg-supergoop-blue ease-in-out duration-500"
                  style={{ width: `${percent}%` }}
                ></div>
              </div>
              <div
                className={`w-full flex justify-between text-grey-5C body-xs ${
                  percent === 100 ? "hidden" : ""
                }`}
              >
                <div>
                  {formatMoney({ amount: 0 }, false).replace(/\.\d+$/, "")}
                </div>
                <div>
                  {formatMoney({ amount: threshold }, false).replace(
                    /\.\d+$/,
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    };
  }, [customerDetails?.email, cart.state, threshold]);
  return states[customerDetails?.email ? "LoggedIn" : "LoggedOut"];
};

export default withCartContext(CartThreshold);
